import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { CONTACT_INFO } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full Name", type: "text", required: true, placeholder: "Jane Contractor" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "you@agency.gov" },
  { name: "company", label: "Company / Agency", type: "text", required: false, placeholder: "Organization" },
  { name: "phone", label: "Phone", type: "tel", required: false, placeholder: "(000) 000-0000" },
];

const inputClass =
  "w-full bg-[#141414] border border-white/10 rounded-none px-4 py-3.5 text-white placeholder:text-white/25 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Your inquiry has been sent.");
      setSent(true);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-[72px]">
      {/* Header */}
      <section data-testid="contact-header" className="relative overflow-hidden grain border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-6">Get in touch</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95] max-w-4xl">
            <MaskLine delay={0.1}>Start the</MaskLine>
            <MaskLine delay={0.22}>
              <span className="text-blue-500">conversation.</span>
            </MaskLine>
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 md:py-24 grid lg:grid-cols-[1fr_1.15fr] gap-14 lg:gap-24">
        {/* Info column */}
        <Reveal>
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-6">
            Tell us about your requirement.
          </h2>
          <p className="text-white/55 text-lg font-light leading-relaxed mb-12 max-w-md">
            Whether it&apos;s a modular facility, a secure build, or a design-build package, our team will
            respond promptly with a path forward.
          </p>

          <div className="space-y-8">
            <div className="flex items-start gap-4" data-testid="contact-address">
              <div className="w-11 h-11 flex items-center justify-center border border-white/10 bg-[#141414] text-blue-400 shrink-0">
                <MapPin size={18} />
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-1">Headquarters</p>
                <p className="text-white/80 text-sm">{CONTACT_INFO.address}</p>
              </div>
            </div>

            {CONTACT_INFO.people.map((p) => (
              <div key={p.email} className="flex items-start gap-4" data-testid={`contact-person-${p.name.split(" ")[0].toLowerCase()}`}>
                <div className="w-11 h-11 flex items-center justify-center border border-white/10 bg-[#141414] text-blue-400 shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-1">
                    {p.name} · {p.title}
                  </p>
                  <a href={`mailto:${p.email}`} className="block text-white/80 text-sm hover:text-blue-400 transition-colors">
                    {p.email}
                  </a>
                  <a href={`tel:${p.phone}`} className="flex items-center gap-1.5 text-white/50 text-sm mt-0.5 hover:text-blue-400 transition-colors">
                    <Phone size={12} /> {p.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-x-10 gap-y-3 font-mono text-xs text-white/40">
            <span>CAGE · {CONTACT_INFO.cage}</span>
            <span>UEI · {CONTACT_INFO.uei}</span>
            <span>DUNS · {CONTACT_INFO.duns}</span>
            <span>GSA · {CONTACT_INFO.gsa}</span>
          </div>
        </Reveal>

        {/* Form column */}
        <Reveal delay={0.15}>
          <div className="border border-white/10 bg-[#0d0d0d] p-6 sm:p-10">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center text-center py-16"
                data-testid="contact-success"
              >
                <CheckCircle2 size={56} className="text-blue-500 mb-6" />
                <h3 className="font-heading font-bold text-2xl mb-3">Message received.</h3>
                <p className="text-white/55 font-light max-w-sm mb-8">
                  Thank you for reaching out to Sabala Management. A member of our team will be in touch shortly.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="send-another-btn"
                  className="px-6 py-3 border border-white/25 hover:border-white text-white font-bold uppercase tracking-wide text-sm transition-colors"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  {FIELDS.map((f) => (
                    <div key={f.name} className={f.name === "message" ? "sm:col-span-2" : ""}>
                      <label
                        htmlFor={f.name}
                        className="block font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2"
                      >
                        {f.label} {f.required && <span className="text-blue-500">*</span>}
                      </label>
                      <input
                        id={f.name}
                        name={f.name}
                        type={f.type}
                        required={f.required}
                        value={form[f.name]}
                        onChange={update}
                        placeholder={f.placeholder}
                        data-testid={`contact-input-${f.name}`}
                        className={inputClass}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2"
                  >
                    Message <span className="text-blue-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={update}
                    placeholder="Describe your project scope, timeline, and location…"
                    data-testid="contact-input-message"
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group w-full flex items-center justify-center gap-2 px-7 py-4 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide text-sm transition-[background-color,transform] hover:scale-[1.01]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Inquiry
                      <Send size={16} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                <p className="text-white/30 text-xs text-center font-light">
                  Your message is delivered directly to our business development team.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
